import BannerMarkdown from '../banners/BannerMarkdown'
import { STRIP_SEVERITY_CLASSES, type BannerSeverity } from '../banners/bannerSeverity'

interface StickyBannerStripProps {
  severity: BannerSeverity
  /** Optional bold lead-in (e.g. "Heads up" for the maintenance banner, or the banner title). */
  title?: string
  message: string
  /** Render `message` as sanitized markdown (dynamic banners) vs plain text (maintenance). */
  renderMarkdown?: boolean
  dismissible: boolean
  onDismiss: () => void
  dismissLabel: string
  /** BannerHost owns `--banner-height`; it passes a ref to measure the visible strip. */
  innerRef?: React.Ref<HTMLDivElement>
  testId?: string
  dataAttrs?: Record<string, string>
  /** Admin preview: render inline (relative) instead of pinned (fixed). */
  preview?: boolean
}

/**
 * Pure presentational full-width sticky top strip. Holds no state, reads no
 * config, and never mutates `--banner-height` — `BannerHost` owns all of that
 * (single-owner invariant). Used for both the env maintenance banner and
 * DB-backed dynamic `display_mode='banner'` banners so they look identical.
 */
export default function StickyBannerStrip({
  severity,
  title,
  message,
  renderMarkdown = false,
  dismissible,
  onDismiss,
  dismissLabel,
  innerRef,
  testId = 'sticky-banner-strip',
  dataAttrs,
  preview = false,
}: StickyBannerStripProps) {
  const classes = STRIP_SEVERITY_CLASSES[severity]
  const SeverityIcon = classes.Icon
  const positioning = preview
    ? 'relative w-full'
    : 'fixed top-0 left-0 right-0 z-[60]'

  return (
    <div
      ref={innerRef}
      role="status"
      aria-live="polite"
      data-testid={testId}
      data-severity={severity}
      {...dataAttrs}
      className={`${positioning} ${classes.container}`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <SeverityIcon
          aria-hidden="true"
          data-testid={`${testId}-icon`}
          className={`w-4 h-4 flex-shrink-0 ${classes.icon}`}
        />
        <span
          className={`text-sm font-medium leading-snug flex-1 text-center ${classes.text}`}
          data-testid={`${testId}-message`}
        >
          {title && <strong className="mr-1">{title}</strong>}
          {renderMarkdown ? <BannerMarkdown>{message}</BannerMarkdown> : message}
        </span>
        {dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            data-testid={`${testId}-dismiss`}
            className={`flex-shrink-0 text-base leading-none px-2 py-1 rounded hover:bg-white/10 ${classes.text}`}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}
